// ADR 11.9.26i §5 — the anonymous arm's refresh-then-replay, for the other
// credential.
//
// `authed` refreshes once and replays once on a 401 (5.9.26p §4). This is the
// same shape applied to the device token: on a non-2xx from an anonymous
// request that **carried a token**, when the response says
// `x-device-token-rejected`, clear the store, enrol again and replay once. A
// second failure surfaces as the error; there is no loop.
//
// It is deliberately narrower than "any 400". The body of a `missing_device_id`
// is a translated sentence with no `code` (`errors.rs`), so the header is the
// only thing worth branching on — and a level-out-of-range 400 carries none,
// so it is never mistaken for a dead token, which matters because a re-enrol
// costs a solve. A 400 with no header on a request that sent no token is a
// client bug (a caller that skipped `ensureDeviceToken()`) and surfaces as the
// error it is.
//
// Why this state exists at all: after a `jwt_secret` rotation every enrolled
// install holds a token that is well-formed, unexpired and signed by a key
// that no longer exists. The phones learn that on the fallback path, where the
// request still succeeds on the legacy UUID and the header says "re-enrol".
// The web sends no UUID (§1), so the same state was a bare 400 — and with
// nothing to branch on, a browser would have kept sending the dead token
// until its `exp`, up to ninety days of a stranger who cannot generate.
//
// Its own file rather than an arm in `http.ts`: that module was at 236 of its
// 300 lines, and the ADR said to move the arm whole rather than trim it to fit.

import { ensureDeviceToken } from '../auth/device'
import { clearDeviceToken, readDeviceToken } from '../auth/deviceStore'
import { ApiError, request, type RequestOptions } from './http'

/** A refused token on a request that actually carried one. */
function rejectedTheTokenWeSent(error: unknown, sent: string | null): error is ApiError {
  return error instanceof ApiError && error.deviceTokenRejected && sent !== null
}

/**
 * `request()`, as a stranger — the device header rides through `requestInit`
 * — with one replay when the server refused the token that rode with it.
 */
export async function anonymousRequest(path: string, options: RequestOptions): Promise<Response> {
  const sent = readDeviceToken()
  try {
    return await request(path, options)
  } catch (error) {
    if (!rejectedTheTokenWeSent(error, sent)) throw error
    // Drop it before re-enrolling so the store and `ensureDeviceToken()` agree
    // — otherwise the "stored, live" arm would hand the dead token straight
    // back and the replay would send exactly what was just refused.
    clearDeviceToken()
    await ensureDeviceToken()
    return await request(path, options)
  }
}
